import { Asset } from '#base/Asset.js';
import { DesignGridWrapper } from '#base/Designer/index.js';
import SheetOneColIcon from '#base/resources/images/icon-one-col-sheet.svg';
import SheetTwoColIcon from '#base/resources/images/icon-two-col-sheet.svg';
import Button from '#base/ui/Button.js';
import Icon from '#base/ui/Icon.js';
import { version } from '#base/version.js';
import { Font as FontImpl, isTextClean, textToDataUrl } from '@c2s-npm/plumlib';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import XLSX from 'xlsx';

const MIN_TEXTAREA_HEIGHT = 34;

const disableEnterButton = (e) => {
  // prevent enter button on single line inputs
  if (e.keyCode === 13) {
    e.preventDefault();
  }
};

export const Font = ({
  selected,
  font,
  text,
  align,
  decoration,
  width = 72,
  height = 72,
  onClick,
  hasPremium,
  elementType,
  children,
  padding = 3,
}) => {
  const { loaded } = font;

  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!font.loaded) {
      font.load(() => setRefresh((s) => s + 1));
    }
  }, [font.loaded]);

  const dataUrl = useMemo(() => {
    return textToDataUrl(text, { font, align, decoration, width, height, elementType, version });
  }, [loaded, font.key, text, align, decoration]);

  return (
    <Asset
      {...{ asset: font, hasPremium }}
      className={classNames(
        `t-flex t-cursor-pointer t-items-center t-justify-center t-gap-3 t-rounded-xl t-border-2 t-transition t-duration-200 t-p-${padding} t-group`,
        {
          't-border-primary': selected,
          't-border-neutrals-light-grey hover:t-border-neutrals-medium-grey': !selected,
        }
      )}
      onClick={() => onClick && onClick(font, decoration)}>
      {loaded && (
        <Icon
          url={dataUrl}
          className={classNames('t-max-h-full t-max-w-full', {
            'group-hover:t-bg-neutrals-medium-grey': !selected,
          })}
          width={width}
          height={height}
          size={Math.min(width, height)}
          color='#B1B3B6'
          hoverColor='var(--plum-primary-color)'
          activeColor='var(--plum-primary-color)'
          isActive={selected}
        />
      )}
      {!loaded && (
        <span className='t-text-center'>
          <i className='fa fa-fw fa-cog fa-spin' />
          <br />
          <span className='t-text-muted'>Loading...</span>
        </span>
      )}
      {children}
    </Asset>
  );
};

export const FontPreview = ({ text, config: { name, file } }) => {
  const [loading, setLoading] = useState(true);

  const font = useMemo(() => {
    const f = new FontImpl(name, { name, file });
    setLoading(true);
    f.load(() => setLoading(false));
    return f;
  }, [file]);

  const dataUrl = useMemo(() => {
    return !loading ? textToDataUrl(text, { font, version }) : '';
  }, [loading, text, font]);

  return (
    <>
      {!loading && dataUrl && <img src={dataUrl} />}
      {(loading || !dataUrl) && (
        <span className='align-middle'>
          <i className='fa fa-fw fa-cog fa-spin' />
          <br />
          <span className='t-font-bold t-text-muted'>Loading...</span>
        </span>
      )}
    </>
  );
};

export const MultiText = ({
  placeholder,
  toolTitle = 'Name',
  data,
  onChange,
  onDelete,
  onAdd,
  csvLabel,
  uploadLabel = 'Add Names',
  label = 'Name',
}) => {
  return (
    <DesignGridWrapper className='t-py-3'>
      <div className='multi-text'>
        {!!label && (
          <div className='t-flex t-items-center t-gap-3'>
            <i className='fa fa-fw fa-blank' />
            <div className='t-flex-auto'>
              <em className='t-font-bold t-not-italic t-text-muted'>{label}</em>
            </div>
            <i className='fa fa-fw fa-blank' />
          </div>
        )}
        {data.map((d, i) => (
          <TextRow
            key={i}
            data={d}
            placeholder={placeholder}
            onChange={(v) => onChange(i, v)}
            onDelete={() => onDelete(i)}
            canDelete={data.length > 1}
          />
        ))}
        <div className='t-mt-3 t-flex t-items-center t-justify-end t-gap-3'>
          <Button
            onClick={onAdd}
            size='xs'
            variant='shadowed-white'
            className='t-cursor-pointer t-whitespace-nowrap !t-font-medium !t-normal-case'>
            <i className='fa fa-fw fa-plus' /> Add {toolTitle}
          </Button>
          <ComboInput onChange={onAdd} />
        </div>

        <div className='t-my-5 t-h-[1px] t-w-full t-bg-gray-400 t-text-center'>
          <span className='t-relative -t-top-3 t-inline-block t-bg-white t-px-2 t-py-1 t-text-xs t-font-normal t-text-brand-black'>
            OR
          </span>
        </div>

        <div className='t-mb-5 t-flex t-items-center t-gap-3'>
          <div className='t-flex t-w-full t-flex-auto t-items-center t-justify-center t-gap-2'>
            <CsvUpload
              onChange={onAdd}
              filename={`MyRTIC - ${csvLabel || label} Template.csv`}
              uploadLabelName={uploadLabel}
              fields={[{ label: csvLabel || label, value: 'data' }]}
            />
          </div>
        </div>
      </div>
    </DesignGridWrapper>
  );
};

export const ComboInput = ({ onChange }) => {
  const ref = useRef();
  const [focus, setFocus] = useState(false);

  const close = useMemo(
    () => (e) => {
      if (!ref.current?.contains(e.target)) {
        setFocus(false);
      }
    },
    []
  );

  useEffect(() => {
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleChange = (e) => {
    const parsed = parseInt(e.target.value);
    if (!isNaN(parsed)) {
      handleSelect(parsed);
    }
  };

  const handleSelect = (v) => {
    onChange && onChange(v);
    setFocus(false);
  };

  return (
    <div ref={ref} className='t-relative t-inline-block t-max-w-[4rem]'>
      <select
        className='t-inline-flex t-cursor-pointer t-appearance-none t-items-center t-justify-center t-rounded-button t-border t-border-solid t-border-gray-300 t-bg-white t-px-3 t-py-2 t-text-center t-font-body t-text-xs t-font-medium t-text-body t-no-underline t-shadow t-duration-300 hover:t-text-body hover:t-no-underline hover:t-opacity-75 focus:t-outline-none active:t-outline-none'
        onChange={handleChange}
        placeholder='#'
        value='#'>
        <option disabled={true} value='#'>
          #
        </option>
        {Array.apply(null, Array(42)).map((e, i) => (
          <option key={i} value={i + 1}>
            {i + 1}
          </option>
        ))}
      </select>
      {/*
      <input
        type='text'
        className='form-control quantity t-text-center'
        onChange={handleChange}
        onFocus={() => setFocus(true)}
        value={value || ''}
        autoComplete='off'
        placeholder={'#'}
      />
      {focus && <div className='t-absolute t-inset-x-0 t-top-full t-drop-shadow t-rounded t-bg-white t-divide-y t-z-10 t-overflow-y-scroll t-max-h-[220px]'>
        {Array.apply(null, Array(42)).map((e,i) => <div key={i} onClick={() => handleSelect(i+1)} className='t-cursor-pointer t-px-2 t-py-1 hover:t-bg-gray-100 t-text-center'>{i+1}</div>)}
      </div>}
      */}
    </div>
  );
};

export const ScrollToMe = () => {
  const ref = useRef();

  useEffect(() => {
    const timeout = setTimeout(() => {
      const findScrollParent = (e) => {
        const p = e.parentElement;
        if (!p) return null;
        else if (p.scrollHeight > p.clientHeight) return p;
        else return findScrollParent(p);
      };
      const parent = findScrollParent(ref.current);
      if (parent && ![document.scrollingElement, document.documentElement, document].includes(parent)) {
        parent.scrollTo(0, ref.current.offsetTop);
      }
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return <a ref={ref} />;
};

export const TextRow = ({ placeholder, data, onChange, onDelete, canDelete }) => {
  return (
    <>
      {!data.data && <ScrollToMe />}
      <div className='t-mb-2 t-flex t-items-center t-gap-3'>
        <a onClick={() => onChange()} className='t-cursor-pointer'>
          <i className={'fa fa-fw fa-eye ' + (data.active ? '' : 'text-muted')} />
        </a>
        <div className='t-flex-auto'>
          <CleanTextArea
            value={data.data}
            placeholder={placeholder}
            handleChange={(e) => onChange({ data: e.target.value })}
            onFocus={() => onChange()}
            type='text'
            forceOneLine
            autoFocus={false}
          />
        </div>
        {canDelete && (
          <a onClick={onDelete} className='t-cursor-pointer'>
            <i className='fa fa-fw fa-close' />
          </a>
        )}
      </div>
    </>
  );
};

const blockTouch = (event) => {
  if (
    document.activeElement.tagName === 'INPUT' ||
    document.activeElement.tagName === 'SELECT' ||
    document.activeElement.tagName === 'TEXTAREA'
  ) {
    event.preventDefault();
  }
};

export const CleanTextArea = (props) => {
  const {
    value,
    placeholder,
    handleChange,
    allowAss = false,
    autoFocus = true,
    forceOneLine,
    className,
    ...restProps
  } = props;

  const isTablet = useMediaQuery({ maxWidth: 1023 });
  var isDirty = !isTextClean(value);
  if (isDirty && value === 'ass' && allowAss) {
    isDirty = false;
  }

  const textareaRef = React.useRef(null);
  const focusRef = useRef({});

  React.useLayoutEffect(() => {
    // Reset height - important to shrink on delete
    textareaRef.current.style.height = 'inherit';
    // Set height
    textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight, MIN_TEXTAREA_HEIGHT)}px`;
  }, [value]);

  useEffect(() => {
    if (forceOneLine) {
      document.querySelector('textarea.single-line-input')?.addEventListener?.('keydown', disableEnterButton);
    }

    return () => {
      document
        .querySelector('textarea.single-line-input')
        ?.removeEventListener?.('keydown', disableEnterButton);
    };
  }, [forceOneLine]);

  useEffect(() => {
    if (!value && autoFocus && !!textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.focus();
      }, 0);
    }

    return () => {
      clearInterval(focusRef.current.interval);
    };
  }, []);

  return (
    <div>
      <textarea
        ref={textareaRef}
        className={classNames(
          'form-control custom-form-control !t-rounded-lg t-border !t-border-neutrals-light-grey !t-text-sm lg:t-text-sm',
          {
            'single-line-input': forceOneLine,
          },
          className
        )}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={handleChange}
        onFocus={(e) => {
          e.target.select();
          if (isTablet) {
            setTimeout(() => window.scrollTo({ behavior: 'instant', top: 0, left: 0 }), 100);
            focusRef.current.interval = setInterval(() => {
              if (focusRef.current.height !== window.innerHeight) {
                focusRef.current.height = window.innerHeight;
                window.scrollTo({ behavior: 'instant', top: 0, left: 0 });
              }
            }, 100);
          }
          /*
              if (isTablet && autoFocus) {
                setTimeout(() => window.scrollTo({ behavior: 'instant', top: 0, left: 0 }), 100);
                // document.body.addEventListener('touchmove', blockTouch, { passive: false });
              }
              */
        }}
        onBlur={() => {
          clearInterval(focusRef.current.interval);
          /*
              if (isTablet) {
                window.scrollTo(window.scrollX, 0);
                // document.body.removeEventListener('touchmove', blockTouch);
              }
              */
        }}
        rows={1}
        style={{
          minHeight: MIN_TEXTAREA_HEIGHT,
          resize: 'none',
        }}
        {...(forceOneLine ? { cols: 1 } : {})}
        {...restProps}
      />

      {isDirty && (
        <div className='t-mt-2 t-font-medium t-text-red-700'>
          We do not allow profanity. Please edit your text.
        </div>
      )}
    </div>
  );
};

const DefaultCsvUploadRenderer = ({
  filename,
  templateUrl,
  handleChange,
  uploadLabelName,
  numOfCols = 1,
}) => {
  const sheetIcon = useMemo(() => {
    switch (numOfCols) {
      case 1:
        return SheetOneColIcon;

      case 2:
        return SheetTwoColIcon;

      default:
        return SheetOneColIcon;
    }
  }, [numOfCols]);

  return (
    <div className='t-justify-beteen t-flex t-items-center t-gap-4'>
      <div className='t-inline-flex t-flex-col t-items-center t-justify-center t-gap-2'>
        <i className='fa fa-file-excel-o !t-text-[20px]' />
        <a
          className='t-inline-flex t-w-[120px] t-cursor-pointer t-items-center t-justify-center t-rounded-lg t-border t-border-neutrals-light-grey t-bg-white t-px-2 t-py-1 t-text-2xs t-font-medium !t-no-underline t-transition t-duration-300 hover:t-border-neutral-200 hover:t-bg-neutral-200 hover:t-text-brand-black'
          download={filename}
          href={templateUrl}>
          <span>Download Template</span>
        </a>
      </div>

      <div className='t-inline-flex t-flex-col t-items-center t-justify-center t-gap-2'>
        <img src={sheetIcon} className='t-h-[20px] t-max-h-full t-w-[20px] t-max-w-full' />
        <span className='t-cursor-pointer t-text-center t-text-2xs t-font-medium'>{uploadLabelName}</span>
      </div>

      <div className='t-inline-flex t-flex-col t-items-center t-justify-center t-gap-2'>
        <i className='fa fa-upload !t-text-[20px]' />
        <span className='t-relative t-inline-flex t-w-[120px] t-cursor-pointer t-items-center t-justify-center t-overflow-hidden t-rounded-lg t-border t-border-neutrals-light-grey t-bg-white t-px-2 t-py-1 t-text-2xs t-font-medium !t-no-underline t-transition t-duration-300 hover:t-border-neutral-200 hover:t-bg-neutral-200'>
          <span>Upload</span>
          <input
            className='t-z-1 t-absolute -t-top-[100px] t-bottom-0 t-left-0 t-right-0 t-block t-cursor-pointer t-opacity-0'
            type='file'
            onChange={handleChange}
          />
        </span>
      </div>
    </div>
  );

  return (
    <div className='t-inline-flex t-items-center t-justify-center t-gap-3'>
      <a
        className='t-inline-flex t-items-center t-gap-2 t-whitespace-nowrap t-text-xs'
        download={filename}
        href={templateUrl}>
        <i className='fa fa-file-excel-o' /> Download Template
      </a>

      <div className='t-relative t-inline-flex t-cursor-pointer t-appearance-none t-items-center t-justify-center t-gap-2 t-rounded-button t-border t-border-solid t-border-gray-300 t-bg-white t-px-3 t-py-2 t-text-center t-font-body t-text-xs t-font-medium t-text-body t-no-underline t-shadow t-duration-300 hover:t-text-body hover:t-no-underline hover:t-opacity-75 focus:t-outline-none active:t-outline-none [&>*]:!t-cursor-pointer'>
        <i className='fa fa-upload' />
        <span>Upload</span>
        <input
          className='t-z-1 t-absolute t-bottom-0 t-left-0 t-right-0 t-top-0 t-block t-opacity-0'
          type='file'
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

export const CsvUpload = ({
  filename = 'Template.csv',
  fields = [{ label: 'Name', value: 'data' }],
  uploadLabelName = 'Add Names',
  onChange,
  renderer: Renderer = DefaultCsvUploadRenderer,
}) => {
  const handleChange = (e) => {
    try {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = function (e) {
        const workbook = XLSX.read(e.target.result);
        let json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
        // if the user has removed the header row, supply our own header
        if (!fields.every((f) => !!json[0][f.label])) {
          json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], {
            header: fields.map((f) => f.label),
          });
        }
        const data = json.map((row) =>
          fields.reduce((a, f) => {
            a[f.value] = '' + row[f.label];
            return a;
          }, {})
        );

        onChange && onChange(data);
      };
      reader.readAsArrayBuffer(file);
    } catch (e) {
      console.error(e);
      alert('There was a problem processing your template file.');
    }
  };

  const templateUrl = useMemo(() => {
    const sheet = XLSX.utils.json_to_sheet([], {
      header: fields.map((f) => f.label),
    });
    const csv = XLSX.utils.sheet_to_csv(sheet);
    const base64 = btoa(csv);
    const url = 'data:text/csv;base64,' + base64;
    return url;
  }, [fields]);

  return <Renderer {...{ filename, templateUrl, handleChange, uploadLabelName, numOfCols: fields.length }} />;
};
